import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { HelpCircle, Calendar, Clock, MessageSquare, Mic } from "lucide-react";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";

function CreateEventPage({ isAuthenticated, userEmail }) {
  const [inputText, setInputText] = useState("");
  const [eventLink, setEventLink] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const navigate = useNavigate();
  const { transcript, resetTranscript } = useSpeechRecognition();

  const handleLogin = async () => {
    try {
      const response = await fetch("https://api.aicalendarassist.com/auth");
      const data = await response.json();
      if (data.auth_url) {
        window.location.href = data.auth_url; 
      }
    } catch (error) {
      console.error("Error fetching auth URL:", error);
    }
  };

  const toggleRecording = () => {
    if (isRecording) {
      SpeechRecognition.stopListening();
      setInputText(transcript);
    } else {
      resetTranscript();
      SpeechRecognition.startListening({ continuous: true });
    }
    setIsRecording(!isRecording);
  };

  const handleSubmit = async () => {
    if (!isAuthenticated) {
      navigate("/");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch("https://api.aicalendarassist.com/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: `user_email=${encodeURIComponent(userEmail)}&user_input=${encodeURIComponent(inputText)}`,
        credentials: "include",
      });
      const data = await response.json();
      //console.log("Response in CHAT endpoint:", data);
      if (data && data.htmlLink) {
        setEventLink(data.htmlLink);
      } else {
        setError("Error creating event, please try again later.");
        // setEventLink("Error creating event.");
      }
    } catch (error) {
      console.error("Error:", error);
      setError("Something went wrong, please try again later.");
      //setEventLink("Error communicating with backend.");
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <main className="min-h-screen">
        <h1 className="main-title">Create Calendar Events with AI</h1>
        <div className="login-section">
          <p className="login-text">Please log in with your Google account to create calendar events.</p>
          <button 
            onClick={handleLogin} 
            className="login-button"
          >
            Login with Google
          </button>
        </div>

        {/* Section to give hints about the creation of the event */}
        <section className="py-12 bg-slate-50 mt-12 rounded-lg">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <div className="bg-slate-200 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <HelpCircle className="text-slate-800" size={28} />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Need Help Creating Your Event?</h2>
              <p className="text-slate-600">Here are some tips to get the best results:</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg">
                <h3 className="text-lg font-semibold mb-2 text-slate-900">1. Event Type</h3>
                <p className="text-slate-600">
                  Start by specifying what kind of event it is: meeting, training, interview, etc.
                  <br />
                  <span className="text-sm italic">Example: "Schedule a job interview..."</span>
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg">
                <h3 className="text-lg font-semibold mb-2 text-slate-900">2. Date & Time</h3>
                <p className="text-slate-600">
                  Include when the event should happen. You can use natural language.
                  <br />
                  <span className="text-sm italic">Example: "...next Monday at 3pm"</span>
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg">
                <h3 className="text-lg font-semibold mb-2 text-slate-900">3. Duration</h3>
                <p className="text-slate-600">
                  Specify how long the event should last.
                  <br />
                  <span className="text-sm italic">Example: "...for 2 hours"</span>
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg">
                <h3 className="text-lg font-semibold mb-2 text-slate-900">4. Attendees</h3>
                <p className="text-slate-600">
                  Include participants' names and emails if needed.
                  <br />
                  <span className="text-sm italic">Example: "...with Sarah (sarah@email.com)"</span>
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg">
                <h3 className="text-lg font-semibold mb-2 text-slate-900">5. Video Conference</h3>
                <p className="text-slate-600">
                  You can either:
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>Provide your existing conference link (Zoom, Microsoft Teams, or other).</li>
                    <li>Or simply request something like "with Google Meet" and we'll create one automatically</li>
                  </ul>
                  <span className="text-sm italic block mt-2">
                    Example: "...with this Zoom link: https://zoom.us/j/example"<br />
                    Or: "...via Google Meet"
                  </span>
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg">
                <h3 className="text-lg font-semibold mb-2 text-slate-900">6. Description</h3>
                <p className="text-slate-600">
                  Add any important details about the event.
                  <br />
                  <span className="text-sm italic">Example: "...about the Q2 marketing strategy"</span>
                </p>
              </div>
            </div>
          </div>
        </section>
        {/* Footer */}
        <footer className="bg-slate-900 text-slate-400 py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="text-xl font-bold text-white mb-4">AI Google Calendar Assistant</div>
              <p className="mb-6">Simplifying scheduling with artificial intelligence</p>

              <div className="mb-4 space-x-4">
                <a href="/privacy-policy" className="hover:underline text-slate-400">
                  Privacy Policy
                </a>
                <a href="/terms" className="hover:underline text-slate-400">
                  Terms & Conditions
                </a>
              </div>

              <div className="text-sm">© {new Date().getFullYear()} AI Calendar Assistant. All rights reserved.</div>
            </div>
          </div>
        </footer>
      </main>
    );
  }

  return (
    <main className="min-h-screen">
      <h1 className="main-title">Create Calendar Events with AI</h1>

      <div className="input-section">
        <textarea
          className="input-textarea"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          placeholder="Enter your calendar request here... (e.g., 'Schedule a meeting with John tomorrow at 2pm for 1 hour')"
        />

        <button onClick={toggleRecording} className="microphone-button">
          <Mic className={isRecording ? "text-red-500 animate-pulse" : "text-black"} />
          {isRecording ? <span className="ml-2 text-red-500">Recording...</span> : null}
        </button>

        <button
          onClick={handleSubmit}
          disabled={loading || !inputText.trim()}
          className="submit-button"
        >
          {loading ? "Processing..." : "Create Event"}
        </button>
      </div>

      {eventLink && (
        <div className="event-link-section">
          <p className="event-link-text">Event created successfully!</p>
          <a
            href={eventLink}
            target="_blank"
            rel="noopener noreferrer"
            className="event-link-button"
          >
            View Event
          </a>
        </div>
      )}

      {error && (
        <div className="error-popup">
          <div className="error-content">
            <span className="close-btn" onClick={() => setError(null)}>&times;</span>
            <p>{error}</p>
          </div>
        </div>
      )}

      {/* Section to give hints about the creation of the event */}
      <section className="py-12 bg-slate-50 mt-12 rounded-lg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="bg-slate-200 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <HelpCircle className="text-slate-800" size={28} />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Need Help Creating Your Event?</h2>
            <p className="text-slate-600">Here are some tips to get the best results:</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-2 text-slate-900">1. Event Type</h3>
              <p className="text-slate-600">
                Start by specifying what kind of event it is: meeting, training, interview, etc.
                <br />
                <span className="text-sm italic">Example: "Schedule a job interview..."</span>
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-2 text-slate-900">2. Date & Time</h3>
              <p className="text-slate-600">
                Include when the event should happen. You can use natural language.
                <br />
                <span className="text-sm italic">Example: "...next Monday at 3pm"</span>
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-2 text-slate-900">3. Duration</h3>
              <p className="text-slate-600">
                Specify how long the event should last.
                <br />
                <span className="text-sm italic">Example: "...for 2 hours"</span>
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-2 text-slate-900">4. Attendees</h3>
              <p className="text-slate-600">
                Include participants' names and emails if needed.
                <br />
                <span className="text-sm italic">Example: "...with Sarah (sarah@email.com)"</span>
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-2 text-slate-900">5. Video Conference</h3>
              <p className="text-slate-600">
                You can either:
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li>Provide your existing conference link (Zoom, Microsoft Teams, or other).</li>
                  <li>Or simply request something like "with Google Meet" and we'll create one automatically</li>
                </ul>
                <span className="text-sm italic block mt-2">
                  Example: "...with this Zoom link: https://zoom.us/j/example"<br />
                  Or: "...with a Google Meet link"
                </span>
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-2 text-slate-900">6. Description</h3>
              <p className="text-slate-600">
                Add any important details about the event.
                <br />
                <span className="text-sm italic">Example: "...about the Q2 marketing strategy"</span>
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-xl font-bold text-white mb-4">AI Google Calendar Assistant</div>
            <p className="mb-6">Simplifying scheduling with artificial intelligence</p>

            <div className="mb-4 space-x-4">
              <a href="/privacy-policy" className="hover:underline text-slate-400">
                Privacy Policy
              </a>
              <a href="/terms" className="hover:underline text-slate-400">
                Terms & Conditions
              </a>
            </div>

            <div className="text-sm">© {new Date().getFullYear()} AI Calendar Assistant. All rights reserved.</div>
          </div>
        </div>
      </footer>
    </main>
  );
}

export default CreateEventPage;